import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import MapTool from "@mapmycustomers/shared/enum/map/MapTool";
import { MapEntity, Territory } from "@mapmycustomers/shared/types/entity";
import { MultiPin } from "@mapmycustomers/shared/types/map";

import { EntityItemProperties } from "@app/component/RecordCard";
import EntityCard from "@app/component/RecordCard/components/EntityCard";
import EntityActions from "@app/scene/map/components/MapMode/components/EntityActions";
import { hideMultiPin, setPinHover } from "@app/scene/map/store/mapMode/actions";
import {
  getMapViewTool,
  getMultiPin,
  getPinHover,
  isRecordsListSortedByDistance,
} from "@app/scene/map/store/mapMode/selectors";
import { getTerritory } from "@app/scene/map/store/territoryMode/selectors";
import { showEntityView } from "@app/store/entityView/actions";
import { RootState } from "@app/store/rootReducer";
import { mapEntityIdGetter } from "@app/util/map/idGetters";

interface Props extends EntityItemProperties<MapEntity> {
  hideMultiPin: typeof hideMultiPin;
  multiPin?: MultiPin;
  pinHover?: string;
  setPinHover: typeof setPinHover;
  showDistance: boolean;
  showEntityView: typeof showEntityView;
  territory?: Territory;
  tool?: MapTool;
}

const TerritoryRecord: React.FC<Props> = ({
  entity,
  hideMultiPin,
  multiPin,
  pinHover,
  setPinHover,
  showDistance,
  showEntityView,
  territory,
  tool,
}) => {
  const intl = useIntl();

  const mapEntity = entity as MapEntity;
  const isHighlighted = mapEntityIdGetter(mapEntity) === pinHover;
  const isQuickActionVisible = isHighlighted && tool === MapTool.LASSO;

  const handleClick = useCallback(() => {
    if (!multiPin) {
      hideMultiPin();
    }

    showEntityView({
      entityId: mapEntity.id,
      entityType: mapEntity.entity,
    });
  }, [hideMultiPin, mapEntity, multiPin, showEntityView]);

  const handleHover = useCallback(() => {
    setPinHover(mapEntityIdGetter(mapEntity));
  }, [mapEntity, setPinHover]);

  const removeActionHoverText = useMemo(
    () =>
      intl.formatMessage({
        id: "map.territory.sidebar.removeFromSelection",
        defaultMessage: "Remove from selection",
        description: "Remove from selection quick action title",
      }),
    [intl]
  );

  if (!territory) {
    return null;
  }

  return (
    <EntityCard
      entity={entity}
      extraActions={<EntityActions />}
      isHighlighted={isHighlighted}
      isRemoveActionVisible={isQuickActionVisible}
      onClick={handleClick}
      onHover={handleHover}
      removeActionHoverText={removeActionHoverText}
      showDistance={showDistance}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  multiPin: getMultiPin(state),
  pinHover: getPinHover(state),
  showDistance: isRecordsListSortedByDistance(state),
  territory: getTerritory(state),
  tool: getMapViewTool(state),
});

const mapDispatchToProps = {
  hideMultiPin,
  setPinHover,
  showEntityView,
};

export default connect(mapStateToProps, mapDispatchToProps)(TerritoryRecord);
